import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getSyncStatus } from '@/api/sync-jobs';

/**
 * Lightweight global poller for "is anything syncing right now?" — the
 * MainLayout uses this to show a banner regardless of which route the user
 * is on. Poll interval is gentle (15s) so an idle workspace doesn't generate
 * load; the Platforms page has its own faster polling for live progress.
 */
export const useSyncStatusStore = defineStore('sync-status', () => {
  const pending = ref(0);
  const recentFailed = ref(0);
  const lastCheckedAt = ref<number>(0);

  let timer: ReturnType<typeof setInterval> | null = null;

  async function refresh() {
    try {
      const s = await getSyncStatus();
      pending.value = s.pending;
      recentFailed.value = s.recent_failed;
      lastCheckedAt.value = Date.now();
    } catch {
      // Silent — banner just stays at last known state on transient failure
    }
  }

  function startPolling() {
    if (timer) return;
    refresh();
    timer = setInterval(refresh, 15_000);
  }
  function stopPolling() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  const hasActivity = computed(() => pending.value > 0);

  return {
    pending,
    recentFailed,
    hasActivity,
    refresh,
    startPolling,
    stopPolling,
  };
});
